import React, { useCallback, useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { archiveEventsPastGracePeriod, isPublicTicketEvent } from "../lib/eventPublicStatus";
import { FEATURED_EVENTS_UPDATED_EVENT, loadFeaturedEventsConfig } from "../utils/featuredEvents";
import { readPublicEventsCache, writePublicEventsCache } from "../utils/publicEventsCache";

const formatEventDate = (value) => {
  if (!value) return "Date TBA";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Date TBA";
  return parsed.toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const HomePage = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();

  const resolveFeaturedEvents = useCallback((eventRows, configured = []) => {
    if (!configured.length) return [];

    const byId = new Map((Array.isArray(eventRows) ? eventRows : []).map((row) => [String(row.id), row]));

    return configured
      .map((item) => {
        const matched = byId.get(String(item.eventId));
        if (!matched) return null;
        if (!isPublicTicketEvent(matched)) return null;

        return {
          ...matched,
          featuredImageUrl: item.bannerUrl || matched.image_url || "",
          formattedDate: formatEventDate(matched.date),
        };
      })
      .filter(Boolean);
  }, []);

  const refreshFeaturedEvents = useCallback(async () => {
    const configured = await loadFeaturedEventsConfig();

    if (!configured.length) {
      setFeaturedEvents([]);
      return;
    }

    const eventIds = configured.map((item) => item.eventId).filter(Boolean);
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .in("id", eventIds)
      .neq("status", "archived");

    if (error) {
      console.error("Failed to load featured event rows:", error);
      setFeaturedEvents([]);
      return;
    }

    await archiveEventsPastGracePeriod(supabase, data || []);
    setFeaturedEvents(resolveFeaturedEvents(data || [], configured));
  }, [resolveFeaturedEvents]);

  const fetchEvents = useCallback(async (options = {}) => {
    const { showRetryState = false } = options;

    if (showRetryState) {
      setRetrying(true);
    }

    setFetchError("");

    const { data, error } = await supabase
      .from("events")
      .select("*")
      .neq("status", "archived")
      .limit(5);

    if (error) {
      console.error("Supabase Error:", error);
      const cachedEvents = readPublicEventsCache().filter((event) => isPublicTicketEvent(event));

      if (cachedEvents.length > 0) {
        await refreshFeaturedEvents();
      } else {
        setFetchError("Server unavailable. Please try again.");
      }
    } else {
      await archiveEventsPastGracePeriod(supabase, data || []);
      const nextEvents = (data || []).filter((event) => isPublicTicketEvent(event));

      if (nextEvents.length > 0) {
        writePublicEventsCache(nextEvents);
      }

      await refreshFeaturedEvents();
      setFetchError("");
    }

    setLoading(false);

    if (showRetryState) {
      setRetrying(false);
    }
  }, [refreshFeaturedEvents]);

  useEffect(() => {
    let isMounted = true;

    const runInitialFetch = async () => {
      if (!isMounted) return;
      await fetchEvents();
    };

    runInitialFetch();

    const subscription = supabase
      .channel("homepage_events")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "events" },
        () => {
          fetchEvents();
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchEvents]);

  useEffect(() => {
    const refreshFeaturedView = async () => {
      await refreshFeaturedEvents();
    };

    const onStorageChange = (event) => {
      if (event.key && event.key !== "echo_jham_featured_events") return;
      refreshFeaturedView();
    };

    window.addEventListener("storage", onStorageChange);
    window.addEventListener(FEATURED_EVENTS_UPDATED_EVENT, refreshFeaturedView);

    return () => {
      window.removeEventListener("storage", onStorageChange);
      window.removeEventListener(FEATURED_EVENTS_UPDATED_EVENT, refreshFeaturedView);
    };
  }, [refreshFeaturedEvents]);

  useEffect(() => {
    if (!featuredEvents.length) {
      setFeaturedIndex(0);
      return;
    }

    if (featuredIndex > featuredEvents.length - 1) {
      setFeaturedIndex(0);
    }
  }, [featuredEvents, featuredIndex]);

  const currentFeatured = featuredEvents[featuredIndex] || null;

  const showPrevFeatured = () => {
    if (featuredEvents.length <= 1) return;
    setFeaturedIndex((prev) => (prev - 1 + featuredEvents.length) % featuredEvents.length);
  };

  const showNextFeatured = () => {
    if (featuredEvents.length <= 1) return;
    setFeaturedIndex((prev) => (prev + 1) % featuredEvents.length);
  };

  const handleOpenFeaturedEvent = async (eventId) => {
    const normalizedEventId = String(eventId || "").trim();
    if (!normalizedEventId) return;

    try {
      const { data, error } = await supabase
        .from("events")
        .select("id, date, time, status")
        .eq("id", normalizedEventId)
        .neq("status", "archived")
        .maybeSingle();

      if (error || !data || !isPublicTicketEvent(data)) {
        navigate("/", {
          replace: true,
          state: {
            homeNotice: "Event no longer available",
          },
        });
        return;
      }

      navigate(`/event/${normalizedEventId}`);
    } catch (err) {
      console.error("Failed to validate featured event before redirect:", err);
      navigate(`/event/${normalizedEventId}`);
    }
  };

  if (fetchError && !loading && featuredEvents.length === 0) {
    return (
      <div className="bg-[#121212] text-white min-h-screen flex items-center justify-center px-6">
        <div className="max-w-lg w-full bg-white/5 border border-white/10 rounded-3xl p-10 text-center">
          <h2 className="text-2xl font-black uppercase italic tracking-tight mb-3">Unable to load homepage</h2>
          <p className="text-sm text-gray-300 mb-8">The server is currently unavailable. Please retry.</p>
          <button
            type="button"
            onClick={() => fetchEvents({ showRetryState: true })}
            disabled={retrying}
            className="bg-brand-maroon hover:bg-red-700 disabled:opacity-60 transition-all px-8 py-3 rounded-full text-[10px] font-black tracking-[0.3em] uppercase shadow-2xl"
          >
            {retrying ? "Retrying..." : "Retry"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] text-white min-h-screen">
      {/* HERO SECTION */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 scale-105"
          style={{ backgroundImage: "url('/assets/home_featured_bg.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-[#121212]" />

        <div className="relative z-10 text-center px-6 max-w-3xl">
          <p className="text-[11px] tracking-[0.5em] uppercase text-brand-white font-black mb-2">
            EchoJham Productions
          </p>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter uppercase italic leading-[0.9]">
            Experience <br /> <span className="text-brand-maroon">The Magic</span>
          </h1>
          <button
            onClick={() => navigate("/events")}
            className="bg-brand-maroon hover:bg-red-700 transition-all px-10 py-4 rounded-full text-[10px] font-black tracking-[0.3em] uppercase shadow-2xl active:scale-95 mt-4"
          >
            Explore Events
          </button>
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-[10px] tracking-[0.5em] font-black text-gray-500 mb-12 uppercase text-center">
            Featured Events
          </h2>

          {loading ? (
            <div className="h-[220px] sm:h-[280px] md:h-[520px] w-full flex items-center justify-center bg-white/5 rounded-3xl animate-pulse">
              <p className="text-gray-500 tracking-widest text-xs">LOADING FEATURED EVENT...</p>
            </div>
          ) : currentFeatured ? (
            <div className="relative group rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl w-full mx-auto">
              <img
                src={currentFeatured.featuredImageUrl || "/assets/featured_event.jpg"}
                alt={currentFeatured.title}
                className="w-full h-[220px] sm:h-[280px] md:h-[550px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none" />

              <button
                type="button"
                onClick={showPrevFeatured}
                disabled={featuredEvents.length <= 1}
                className="absolute z-30 left-2 md:left-4 top-1/2 -translate-y-1/2 h-8 w-8 md:h-10 md:w-10 rounded-full bg-black/60 hover:bg-black text-white text-lg font-black pointer-events-auto disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous featured event"
              >
                ‹
              </button>

              <button
                type="button"
                onClick={showNextFeatured}
                disabled={featuredEvents.length <= 1}
                className="absolute z-30 right-2 md:right-4 top-1/2 -translate-y-1/2 h-8 w-8 md:h-10 md:w-10 rounded-full bg-black/60 hover:bg-black text-white text-lg font-black pointer-events-auto disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next featured event"
              >
                ›
              </button>

              <div className="absolute bottom-0 left-0 p-4 md:p-12 w-full">
                <div className="flex items-center gap-2 text-white/90 text-[10px] font-black uppercase tracking-widest mb-3">
                  <MapPin size={14} />
                  {currentFeatured.venue || "Venue TBA"}
                </div>

                <p className="text-[11px] text-gray-300 font-semibold uppercase tracking-wider mb-3">
                  {currentFeatured.formattedDate}
                </p>

                <h3 className="text-2xl md:text-5xl font-black mb-4 md:mb-6 uppercase italic leading-none max-w-4xl">
                  {currentFeatured.title}
                </h3>

                <button
                  onClick={() => handleOpenFeaturedEvent(currentFeatured.id)}
                  className="bg-white text-black hover:bg-brand-maroon hover:text-white px-6 md:px-8 py-2.5 md:py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-lg"
                >
                  View Event
                </button>

                {featuredEvents.length > 1 ? (
                  <div className="mt-6 flex items-center gap-2">
                    {featuredEvents.map((item, index) => (
                      <button
                        key={`${item.id}-${index}`}
                        type="button"
                        onClick={() => setFeaturedIndex(index)}
                        className={`h-2.5 rounded-full transition-all ${index === featuredIndex ? "w-6 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
                        aria-label={`Go to featured event ${index + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
              <p className="text-gray-500 uppercase text-xs tracking-widest">No featured events available.</p>
            </div>
          )}

          {fetchError ? (
            <div className="mt-6 text-center">
              <p className="text-[11px] uppercase tracking-wider text-gray-500">
                Featured content refresh failed. Showing last successful data.
              </p>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default HomePage;